import Typography from "@mui/material/Typography";

import { UpsellModuleTitle } from "@app/analytics/components/UpsellModule";
import {
  CHANNEL_OPTS_FOR_TOPIC_PROVIDER,
  MESSAGE_OPTS_FOR_TOPIC_PROVIDER,
} from "@app/analytics/constants/topics";
import { PRODUCTIVITY_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import LINKS from "@app/common/links";
import {
  ProductivityIntegrationStatus,
  ProductivityIntegrationStatusStep,
} from "@app/common/types";
import { pluralizePhrase } from "@app/common/utils";

import Link from "@rui/Link";
import { Column, Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

import {
  ActionItemCountChip,
  CountChip,
  KeyQuestionCountChip,
  ProgressIcon,
} from "./styles";
import { getStepDescription } from "./utils";

const NUM_VISIBLE_STEPS = ProductivityIntegrationStatusStep.COMPLETE - 1;

const ReadoutStatusContent = ({
  status,
  step,
}: {
  status: ProductivityIntegrationStatus;
  step: number;
}) => {
  const providerConfig = PRODUCTIVITY_PROVIDERS_BY_ID[status.provider];
  const messageOpts = MESSAGE_OPTS_FOR_TOPIC_PROVIDER[status.provider];
  const channelOpts = CHANNEL_OPTS_FOR_TOPIC_PROVIDER[status.provider];

  return (
    <>
      <UpsellModuleTitle
        Icon={
          <ProgressIcon
            error={status.error}
            iconSrc={providerConfig.icon}
            step={step}
          />
        }
      >
        {status.error
          ? `No ${providerConfig.displayNameShort} Readout Generated`
          : `Generating ${providerConfig.displayNameShort} Readout`}
      </UpsellModuleTitle>
      {status.error ? (
        <Typography variant="labelReg" color="shades.neutral.600">
          We couldn't generate a Readout from {providerConfig.displayNameShort}.
          This may be due to limited emails or using an unsupported language.
          For more details, please check our{" "}
          <Link href={LINKS.SUPPORT.NO_READOUTS} underline fontWeight={400}>
            support article
          </Link>
          .
        </Typography>
      ) : (
        <Column spacing={0.5}>
          <Typography variant="labelReg" color="shades.neutral.600">
            {step <= NUM_VISIBLE_STEPS && (
              <Bold sx={{ color: "shades.primary.500" }}>
                Step {step} of {NUM_VISIBLE_STEPS}:{" "}
              </Bold>
            )}
            {getStepDescription(step, providerConfig)}
          </Typography>
          <Row spacing={0.5} variant="left" flexWrap="wrap">
            {messageOpts && (
              <CountChip
                Icon={messageOpts.Icon}
                count={status.numMessages}
                label={messageOpts.sourceLabelShort}
                tooltip={`Analyzed ${pluralizePhrase(
                  status.numMessages ?? 0,
                  messageOpts.sourceLabelShort
                )}`}
              />
            )}
            {channelOpts && (
              <CountChip
                Icon={channelOpts.Icon}
                count={status.numChannels}
                label={channelOpts.sourceLabelShort}
                tooltip={`Analyzed ${pluralizePhrase(
                  status.numChannels ?? 0,
                  channelOpts.sourceLabelShort
                )}`}
              />
            )}
            <ActionItemCountChip count={status.numActionItems} />
            <KeyQuestionCountChip count={status.numKeyQuestions} />
          </Row>
        </Column>
      )}
    </>
  );
};

export default ReadoutStatusContent;
